<template>
  <div class="ion-page">
    <ion-header class="ion-no-border">
      <ion-toolbar class="ion-no-border toolbar-transparent">
        <ion-buttons slot="end">
          <ion-button @click="onCancel">
            <ion-icon slot="start" name="close"></ion-icon>
          </ion-button>
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="ion-padding">
      <transition-group
        tag="div"
        v-touch:swipe="swipeHandler"
        v-touch-options="{ swipeTolerance: 80 }"
        class="step-container"
        v-bind="swipeTransitionProps"
      >
        <FormStep
          v-for="component of components"
          :key="component.id"
          :component="component"
          :errors="errors"
          :loading="loading"
          @next="next"
          @prev="prev"
          @submit="onSubmit"
          @check="checkErrors"
          v-model="data"
        />
      </transition-group>
    </ion-content>
    <ion-footer class="ion-no-border">
      <div class="ion-padding">
        <div class="slider-nav">
          <div class="slider-nav-element slider-nav-progress-container">
            <div class="text-container-sm">
              <span>{{ step + 1 }} of {{ formLength }}&nbsp;&nbsp;</span>
              <ion-chip color="primary" @click="jumpToIncomplete">
                <ion-label>{{ formProgressPercentage }}% complete</ion-label>
              </ion-chip>
            </div>
            <ion-range
              ref="range"
              min="1"
              :max="formLength"
              step="1"
              snaps="true"
              color="primary"
              :value="step + 1"
              pin="true"
              ticks="false"
              class="slider-nav-range-controller"
            ></ion-range>
          </div>
          <div class="slider-nav-element">
            <div class="button-group">
              <button @click="prev">
                <ion-icon name="arrow-back" />
              </button>
              <button @click="next">
                <ion-icon name="arrow-forward" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </ion-footer>
  </div>
</template>

<script>
import { debounce } from "lodash";
import { DateTime, Duration } from "luxon";
import superagent from "superagent";

import FormStep from "../FormStep.vue";

export default {
  components: {
    FormStep
  },
  props: ["form", "asset", "state"],
  data() {
    return {
      errors: {},
      loading: false,
      swipeIsForward: true
    };
  },
  mounted() {
    this.setupRangeController();
  },
  computed: {
    data: {
      get() {
        return this.state.data;
      },
      set(value) {
        this.$emit("update:state", { ...this.state, data: value });
      }
    },
    step: {
      get() {
        return this.state.step;
      },
      set(value) {
        this.$emit("update:state", { ...this.state, step: value });
      }
    },
    components() {
      const components = [this.form.components[this.step]];
      return components;
    },
    swipeTransitionProps() {
      return {
        name: "custom-classes-transition",
        "enter-active-class": this.swipeIsForward
          ? "animated fadeInRight"
          : "animated fadeInLeft",
        "leave-active-class": this.swipeIsForward
          ? "animated fadeOutLeft"
          : "animated fadeOutRight",
        mode: "out-in"
      };
    },
    formProgress() {
      const totalCompleted = Object.values(this.data).filter(
        v => v != undefined
      ).length;
      return totalCompleted / this.formLength;
    },
    formProgressPercentage() {
      return (this.formProgress * 100).toFixed();
    },
    formLength() {
      return this.form.components.length;
    },
    formInvalid() {
      return Object.keys(this.errors).length > 0;
    }
  },
  methods: {
    setupRangeController() {
      setTimeout(() => {
        const range = this.$refs.range;
        if (range) {
          const handleChange = debounce(function(e) {
            this.jumpToStep(e.target.value - 1);
          }, 200).bind(this);
          const handleBlur = function(e) {
            this.jumpToStep(e.target.value - 1);
          }.bind(this);
          range.addEventListener("ionChange", handleChange);
          range.addEventListener("ionBlur", handleBlur);
        }
      }, 200);
    },
    swipeHandler(direction) {
      switch (direction) {
        case "right": {
          this.prev();
          break;
        }
        case "left": {
          this.next();
          break;
        }
      }
    },
    checkErrors(value, id) {
      if (this.errors[id] && value != undefined) {
        this.$delete(this.errors, id);
      }
    },
    validate() {
      this.errors = {};
      for (let i = 0; i < this.form.components.length; i++) {
        const component = this.form.components[i];
        this.validateComponent(component);
      }
      if (this.formInvalid) {
        this.jumpToInvalid();
        return false;
      }
      return true;
    },
    validateComponent(component) {
      const value = this.data[component.id];
      if (component.valid && component.valid.required && !value) {
        this.$set(this.errors, component.id, "Please complete");
      }
    },
    jumpToStep(i) {
      if (i >= 0 && i < this.formLength) {
        if (i > this.step) {
          this.swipeIsForward = true;
        }
        if (i < this.step) {
          this.swipeIsForward = false;
        }
        this.step = i;
      }
    },
    jumpToInvalid() {
      const index = this.getFirstErrorIndex();
      if (index != undefined) {
        this.jumpToStep(index);
      } else {
        this.errors = {};
      }
    },
    jumpToIncomplete() {
      const index = this.getFirstIncompleteIndex();
      if (index != undefined) {
        this.validateComponent(this.form.components[index]);
        this.jumpToStep(index);
      }
    },
    getFirstIncompleteIndex() {
      return this.form.components.findIndex(
        component => this.data[component.id] == undefined
      );
    },
    getFirstErrorIndex() {
      const keys = Object.keys(this.errors);
      return this.form.components.findIndex(
        component => component.id === keys[0]
      );
    },
    onCancel() {
      return this.$ionic.alertController
        .create({
          header: "Changes will be lost",
          message:
            "Are you sure you would like to cancel? Your current progress will be lost.",
          buttons: [
            {
              text: "Discard",
              handler: () => {
                this.$emit("cancel");
              },
              cssClass: "secondary"
            },
            {
              text: "Resume",
              role: "cancel"
            }
          ]
        })
        .then(a => a.present());
    },
    getFormResult() {
      const fails = {
        critical: [],
        warn: []
      };
      for (let i = 0; i < this.form.components.length; i++) {
        const component = this.form.components[i];
        const category = (component.section || {}).type;
        const pass = component.pass;
        if (pass && category) {
          let fail = false;
          let value;
          if (pass.field) {
            value = this.data[component.id][pass.field];
          } else {
            value = this.data[component.id];
          }
          if (pass.value) {
            if (value !== pass.value) {
              fail = true;
            }
          }
          if (component.type === "date") {
            if (typeof pass.date_today_distance === "object") {
              const distance = DateTime.fromISO(value).diffNow(
                Object.keys(component.pass.date_today_distance)
              );
              const duration = Duration.fromObject(
                component.pass.date_today_distance
              );
              if (distance.as("weeks") > duration.as("weeks")) {
                fail = true;
              }
            }
          }
          if (fail) {
            fails[category].push({ component, value });
          }
        }
      }
      if (fails.critical.length > 0) {
        return "critical";
      } else if (fails.warn.length > 0) {
        return "warn";
      } else {
        return "pass";
      }
    },
    getFormData() {
      const data = new FormData();

      const answers = [];
      Object.keys(this.data).forEach(key => {
        const component = this.form.components.find(c => c.id === key);
        const value = this.data[key];
        answers.push({ component, value });
      });

      data.append("answers", JSON.stringify(answers));

      const files = this.$store.state.files;
      Object.keys(files).forEach(key => {
        const file = files[key];
        data.append(key, file);
      });

      if (this.asset && this.asset._id) {
        data.append("asset_id", this.asset._id);
      }

      return data;
    },
    submitForm(data) {
      return superagent
        .post(
          process.env.VUE_APP_API_ORIGIN +
            "/api/v1/inspection-forms/submit/" +
            this.form._id
        )
        .send(data)
        .then();
    },
    handleError(message = "An unexpected error occured!") {
      const timeout = 10000; // 10s
      this.$ionic.toastController
        .create({
          message,
          color: "danger",
          position: "bottom",
          duration: timeout
        })
        .then(toast => toast.present());
    },
    async onSubmit() {
      this.loading = true;
      try {
        if (this.validate()) {
          const data = this.getFormData();
          switch (this.form.type) {
            case "prestart": {
              const result = this.getFormResult();
              this.$emit("result", result);
              data.append("result", result);
              await this.submitForm(data);
              this.$emit("submit", data);
              break;
            }
            case "hire": {
              await this.submitForm(data);
              this.$emit("submit", data);
              break;
            }
          }
        }
      } catch (err) {
        this.handleError("Please check your internet connection and try again");
      }
      this.loading = false;
    },
    next() {
      if (this.step < this.formLength) {
        this.swipeIsForward = true;
        this.step += 1;
      }
    },
    prev() {
      if (this.step > 0) {
        this.swipeIsForward = false;
        this.step -= 1;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.button-group {
  white-space: nowrap;
  button {
    background-color: rgb(244, 245, 248);
    padding: 5px 20px;
    font-weight: 500;
    font-size: 20px;
    outline: 0;
    line-height: 50%;
    &:first-of-type {
      border-top-left-radius: 10px;
      border-bottom-left-radius: 10px;
    }
    &:last-of-type {
      border-top-right-radius: 10px;
      border-bottom-right-radius: 10px;
    }
    &:not(:first-of-type) {
      border-left: 0.55px solid rgba(var(--ion-text-color-rgb, 0, 0, 0), 0.2);
    }
  }
}

.slider-nav {
  display: flex;
  flex-direction: row;
  &-element {
    padding-left: 5px;
    padding-right: 5px;
    padding-bottom: 5px;
    font-size: 0.8em;
    display: flex;
    justify-content: flex-end;
    flex-direction: column;
  }
  &-progress-container {
    flex-grow: 1;
    display: flex;
    justify-content: center;
    flex-direction: column;
    white-space: nowrap;
  }
  &-range-controller {
    padding-bottom: 0px;
    padding-top: 0px;
    padding-left: 0px;
    margin-bottom: -7px;
  }
}

.step-container {
  height: 100%;
  position: relative;
}
</style>
